import { Link, useLocation } from '@tanstack/react-router';
import { Button } from '@upcaret/ui';
import { CentlyWordmark } from '../../../components/CentlyWordmark';
import {
  CENTLY_APP_STORE_URL,
  CENTLY_GOOGLE_PLAY_URL,
} from '../../../constants/app-links';
import { LEAN_ESCAPE_ROUTES } from './site-pages';
import './not-found.css';

type NotFoundPageProps = {
  /** When true, header/footer come from the marketing layout route. */
  embedded?: boolean;
};

function formatAttemptedPath(pathname: string): string {
  if (!pathname || pathname === '/') return '/';
  return pathname;
}

export function NotFoundPage({ embedded = false }: NotFoundPageProps) {
  const { pathname } = useLocation();
  const attemptedPath = formatAttemptedPath(pathname);

  const content = (
    <div className="not-found-enter mx-auto w-full min-w-0 max-w-3xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8">
      <div className="mx-auto max-w-3xl text-center">
        <p className="m-0 inline-flex items-center gap-2 rounded-full border border-border bg-muted px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-primary">
          <span aria-hidden="true">404</span>
          Page not found
        </p>
        <h1 className="mt-4 text-balance text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
          That link took a detour.
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-pretty text-base leading-relaxed text-muted-foreground sm:text-lg">
          We couldn’t find that page. The link might be outdated, or the URL
          might have a typo. Either way, we’ll help you get back on track.
        </p>
        <p className="mx-auto mt-4 max-w-2xl text-sm text-muted-foreground">
          You were headed to{' '}
          <code className="rounded-md border border-border bg-muted px-2 py-1 text-xs text-foreground sm:text-sm">
            {attemptedPath}
          </code>
        </p>
      </div>

      <section className="mt-9 rounded-2xl border border-border bg-card p-5 shadow-xs sm:p-6">
        <h2 className="text-lg font-semibold text-foreground">
          Try one of these pages
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          These links are live and can get you back on track quickly.
        </p>
        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {LEAN_ESCAPE_ROUTES.map((route) => (
            <Button
              key={route.path}
              asChild
              variant={route.path === '/' ? 'default' : 'outline'}
              className="min-h-11 w-full"
            >
              <Link to={route.path}>{route.label}</Link>
            </Button>
          ))}
        </div>
      </section>

      <p className="mt-6 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center text-sm leading-none text-muted-foreground">
        <span>Prefer the app?</span>
        <a
          href={CENTLY_APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-primary underline-offset-4 hover:underline"
        >
          App Store
        </a>
        <span aria-hidden="true">·</span>
        <a
          href={CENTLY_GOOGLE_PLAY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-primary underline-offset-4 hover:underline"
        >
          Google Play
        </a>
      </p>
    </div>
  );

  if (embedded) {
    return content;
  }

  const currentYear = new Date().getFullYear();

  return (
    <div className="flex min-h-screen min-w-0 flex-col bg-background font-sans antialiased">
      <header className="flex h-16 items-center px-4 md:px-6">
        <Link className="flex items-center justify-center" to="/">
          <CentlyWordmark className="text-3xl" />
        </Link>
      </header>
      <main className="min-w-0 flex-1">{content}</main>
      <footer className="flex w-full shrink-0 items-center px-4 py-6 md:px-6">
        <p className="text-xs text-muted-foreground">
          &copy; {currentYear} Cently. All rights reserved.
        </p>
      </footer>
    </div>
  );
}

export default NotFoundPage;
